import { useCallback, useEffect, useMemo, useState } from "react";

import {
  hasHardStop,
  mapValidationAction,
  VALIDATION_ACTION,
} from "../../../utils/vaccineValidations";

const getRowKey = (v, index) => `${v?.rule ?? "NO_RULE"}|${index}`;

export function useVaccineValidationsModal({ open, validations }) {
  const items = useMemo(
    () => (Array.isArray(validations) ? validations : []),
    [validations]
  );

  const hardStop = useMemo(() => hasHardStop(items), [items]);

  const [responses, setResponses] = useState({});

  useEffect(() => {
    if (!open) return;
    setResponses(
      Object.fromEntries(
        items.map((v, index) => [getRowKey(v, index), { acknowledged: false, reason: "" }])
      )
    );
  }, [open, items]);

  const isComplete = useMemo(() => {
    if (hardStop || items.length === 0) return false;

    return items.every((v, index) => {
      const key = getRowKey(v, index);
      const action = mapValidationAction(v?.action);
      const r = responses[key] || {};

      if (action === VALIDATION_ACTION.ENTER_REASON_STOP) {
        return Boolean(String(r.reason || "").trim());
      }
      if (action === VALIDATION_ACTION.YES_NO_CONTINUE) {
        return Boolean(r.acknowledged);
      }
      return true;
    });
  }, [hardStop, items, responses]);

  const getItemState = useCallback(
    (v, index) => {
      const key = getRowKey(v, index);
      const action = mapValidationAction(v?.action);
      const response = responses[key] || {};
      return {
        key,
        action,
        isHard: action === VALIDATION_ACTION.HARD_STOP,
        needsReason: action === VALIDATION_ACTION.ENTER_REASON_STOP,
        needsAck: action === VALIDATION_ACTION.YES_NO_CONTINUE,
        response,
      };
    },
    [responses]
  );

  const toggleAck = useCallback((key) => {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...prev[key], acknowledged: !prev[key]?.acknowledged },
    }));
  }, []);

  const setReason = useCallback((key, value) => {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...prev[key], reason: value },
    }));
  }, []);

  return {
    items,
    hardStop,
    isComplete,
    getItemState,
    toggleAck,
    setReason,
  };
}

